import { FC, Fragment, useEffect } from "react";
import { observer } from "mobx-react";
import { AccountCompanyDetails, isFailure, isOk, isPending } from "@/common/common";
import {
  useDispatch,
  useLanguage,
  useLocaleFormat,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { TMessage } from "@/i18n/tt";
import { Alert } from "@/components-v2/Alert";
import { Box, Container, Grid, VBox } from "@/components-v2/Box";
import { CloseButton } from "@/components-v2/Button";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { Icon } from "@/components-v2/Icon";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { ResizableSidepanelLayout } from "@/components-v2/ResizableSidepanelLayout";
import { MSelect } from "@/components-v2/Select";
import { Tabs } from "@/components-v2/Tabs";
import { Tag } from "@/components-v2/Tag";
import { MTextInput } from "@/components-v2/TextInput";
import {
  AddFieldDialog,
  EditValueDialog,
  FieldValue,
  ImportResultDialog,
  ValueHistoryDialog,
} from "./customFieldsComponents";
import {
  customFieldDataTypes,
  CustomFieldDefinition,
  dataTypeIcons,
  dataTypeLabels,
  numberFormatSample,
  permissionLabels,
  requirementLabels,
  subjectDisplayName,
  subjectRoleLabel,
  toCompanyCustomField,
  validationEntries,
  validationLabels,
  contactRoleLabels,
} from "./customFieldsDomain";
import {
  AccountFieldsViewProps,
  DeleteFieldViewProps,
  FieldPanelViewProps,
  FieldValueRow,
  initAccountFields,
  prepAccountFieldsView,
} from "./accountFieldsDomain";
import { Dialog } from "@/components-v2/Dialog";
import { excelUploadConfig } from "@/fileUpload/fileUploadDomain";
import { EntityDataImportDialog } from "./entityDataImportComponents";
import { CompanyRoleImportDialog } from "./companyRoleImportComponents";
import { OwnershipImportDialog } from "@/ownershipImport/ownershipImportComponents";
import { FieldHistoryImportDialog } from "./fieldHistoryImportComponents";
import { Markdown } from "@/text/Markdown";
import type { AccountRoleType } from "@/companyRoles/accountRoleTypeApiClient";

export const AccountFieldsSection: FC<{
  accountId: string;
  accountName: string;
  companies: AccountCompanyDetails[];
  editDefinitionId?: string;
}> = observer(({ accountId, accountName, companies, editDefinitionId }) => {
  const rootStore = useRootStore();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      initAccountFields(
        accountId,
        accountName,
        companies.map((company) => ({ orgNo: company.orgNo, name: company.companyName })),
        editDefinitionId,
      ),
    );
  }, [dispatch, accountId, accountName, companies, editDefinitionId]);
  const { lang } = useTranslation();
  const props = prepAccountFieldsView({ state: rootStore.accountFields, dispatch, lang });
  return props ? <AccountFieldsView {...props} /> : null;
});

export const AccountFieldsView: FC<AccountFieldsViewProps> = observer((props) => (
  <ResizableSidepanelLayout
    containerProps={{ overflow: "hidden" }}
    centerPanel={<FieldsMainPanel {...props} />}
    rightBaseSizePercent={45}
    rightPanel={
      props.fieldPanel && (
        <FieldPanel
          panel={props.fieldPanel}
          onEditField={props.onEditField}
          onClose={props.onCloseFieldPanel}
        />
      )
    }
  />
));

const FieldsMainPanel: FC<AccountFieldsViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <Container marginTop={"lg"} maxWidth={"xl"} gap={"lg"}>
      <h3>{t("enterprise-account.fields")}</h3>
      <Tabs
        tabs={[
          {
            id: "entity",
            label: t("entity-data.tab-entity"),
            isActive: props.tab === "entity",
            onSelect: () => props.onSelectTab("entity"),
          },
          {
            id: "board-and-ceo",
            label: t("entity-data.tab-company-roles"),
            isActive: props.tab === "board-and-ceo",
            onSelect: () => props.onSelectTab("board-and-ceo"),
          },
        ]}
      />
      <FieldsList {...props} />
      {props.addFieldModel && (
        <AddFieldDialog
          model={props.addFieldModel}
          accountName={props.accountName}
          onSubmitAddField={props.onSubmitAddField}
          onCloseAddField={props.onCloseAddField}
          onDeleteField={props.addFieldModel.definition ? props.onDeleteField : undefined}
        />
      )}
      {props.deleteField && <DeleteFieldDialog deleteField={props.deleteField} />}
      {props.importResult && (
        <ImportResultDialog result={props.importResult} onClose={props.onCloseImportResult} />
      )}
      <EntityDataImportDialog />
      <CompanyRoleImportDialog />
      <OwnershipImportDialog />
      <FieldHistoryImportDialog />
    </Container>
  );
});

const FieldsList: FC<AccountFieldsViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <>
      <Box justifyContent={"space-between"} alignItems={"center"} gap={"md"}>
        <Box>
          <MTextInput
            model={props.searchModel}
            placeholder={t("search")}
            icon={"magnifying-glass"}
          />
          <MSelect model={props.typeFilter} />
        </Box>
        <Box alignItems={"center"} gap={"md"}>
          {props.tab === "entity" && <ExcelMenu {...props} />}
          <DropdownMenu
            button={{
              type: "button",
              variant: "secondary",
              icon: "upload",
              label: { key: "entity-data.spreadsheet-menu" },
            }}
            items={[
              {
                type: "file-upload",
                label: { key: "entity-data.import-entity-data" },
                fileUploadConfig: excelUploadConfig,
                onSelect: (files) => props.onImportEntityData(files),
              },
              {
                type: "file-upload",
                label: { key: "entity-data.import-company-roles" },
                fileUploadConfig: excelUploadConfig,
                onSelect: (files) => props.onImportCompanyRoles(files),
              },
              {
                type: "file-upload",
                label: { key: "ownership-import.title" },
                fileUploadConfig: excelUploadConfig,
                onSelect: (files) => props.onImportOwnership(files),
              },
              {
                type: "file-upload",
                label: { key: "field-history-import.title" },
                fileUploadConfig: excelUploadConfig,
                onSelect: (files) => props.onImportFieldHistory(files),
              },
            ]}
          />
          <DropdownMenu
            button={{
              type: "button",
              variant: "secondary",
              icon: "plus",
              label: { key: "entity-data.add-field" },
            }}
            items={customFieldDataTypes.map((dataType) => ({
              type: "item",
              icon: dataTypeIcons[dataType],
              label: dataTypeLabels[dataType],
              onSelect: () => props.onAddField(dataType),
            }))}
          />
        </Box>
      </Box>
      {isPending(props.definitionsResult) && <LoadingIndicator />}
      {isFailure(props.definitionsResult) && <Alert type={"error"} message={t("general-error")} />}
      {isOk(props.definitionsResult) &&
        (props.definitions.length === 0 ? (
          <Box fg={"subtle"}>{t("entity-data.no-fields")}</Box>
        ) : (
          <DefinitionsTable {...props} />
        ))}
    </>
  );
});

const ExcelMenu: FC<AccountFieldsViewProps> = observer((props) =>
  props.isImporting ? (
    <LoadingIndicator />
  ) : (
    <DropdownMenu
      button={{ type: "link", label: { key: "excel.edit-with-excel" } }}
      items={[
        {
          type: "item",
          icon: "download",
          label: { key: "excel.download" },
          onSelect: props.onExportFields,
        },
        props.uploadConfig && {
          type: "file-upload",
          label: { key: "excel.upload" },
          fileUploadConfig: props.uploadConfig,
          onSelect: (files) => props.onUploadFields(files),
        },
      ]}
    />
  ),
);

const DeleteFieldDialog: FC<{ deleteField: DeleteFieldViewProps }> = observer(({ deleteField }) => {
  const { t } = useTranslation();
  const { definition, valuesResult, rows } = deleteField;
  return (
    <Dialog
      isSubDialog
      maxWidth={"sm"}
      title={t("entity-data.delete-field-confirm-title")}
      onCancel={deleteField.onCancel}
      onSubmit={deleteField.onConfirm}
      isSubmitting={deleteField.isDeleting}
      isSubmitDisabled={!isOk(valuesResult)}
      submitLabel={t("entity-data.delete-field-forever")}
      submitColor={"error"}
    >
      <VBox gap={"md"}>
        {isPending(valuesResult) && <LoadingIndicator />}
        {isFailure(valuesResult) && <Alert type={"error"} message={t("general-error")} />}
        {isOk(valuesResult) && (
          <Markdown
            value={t({
              key:
                rows.length === 0
                  ? "entity-data.delete-field-description-none"
                  : "entity-data.delete-field-description",
              params: { name: definition.name, count: rows.length },
            })}
          />
        )}
        {rows.length > 0 && (
          <VBox overflowY={"auto"} className={"max-h-80"}>
            {rows.map((row) => (
              <Box
                key={row.subject}
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={"lg"}
                paddingY={"sm"}
                className={"border-b border-gray"}
              >
                <span>{row.label}</span>
                <Box fg={"subtle"} className={"italic"}>
                  <FieldValue
                    value={row.value}
                    selectOptions={definition.selectOptions}
                    numberFormat={definition.numberFormat}
                  />
                </Box>
              </Box>
            ))}
          </VBox>
        )}
        {deleteField.error && <Alert type={"error"} message={t(deleteField.error)} />}
      </VBox>
    </Dialog>
  );
});

const ActivationTag: FC<{ definition: CustomFieldDefinition }> = observer(({ definition }) => {
  switch (definition.companyScope) {
    case "ALL_COMPANIES":
      return <Tag color={"success"} label={{ key: "all-companies" }} />;
    case "SELECTED_COMPANIES":
      return <Tag color={"info"} label={String(definition.selectedOrgNos.length)} />;
    case "HIDDEN":
      return <Tag color={"neutral"} label={{ key: "hidden" }} />;
  }
});

const DefinitionsTable: FC<AccountFieldsViewProps> = observer(
  ({ definitions, fieldPanel, onEditField, onSelectField }) => {
    const { t } = useTranslation();
    const columns: TableColumn<CustomFieldDefinition>[] = [
      {
        name: "name",
        label: { key: "entity-data.field-name" },
        getValue: (definition) => (
          <Box alignItems={"center"} gap={"md"}>
            <Icon name={dataTypeIcons[definition.dataType]} />
            <VBox>
              <span>{definition.name}</span>
              {definition.description && (
                <Box className={"subtle"} textSize={"sm"}>
                  {definition.description}
                </Box>
              )}
            </VBox>
          </Box>
        ),
        align: "left",
        getSortValue: (definition) => definition.name,
      },
      {
        name: "activations",
        label: { key: "entity-data.activations" },
        getValue: (definition) => <ActivationTag definition={definition} />,
        align: "left",
      },
      {
        name: "type",
        label: { key: "type" },
        getValue: (definition) => t(dataTypeLabels[definition.dataType]),
        align: "left",
      },
      {
        name: "actions",
        label: "",
        stopClick: true,
        getValue: (definition) => (
          <Box justifyContent={"end"}>
            <DropdownMenu
              button={{ type: "dotdotdot" }}
              items={[
                {
                  type: "item",
                  icon: "edit",
                  label: { key: "entity-data.edit-field" },
                  onSelect: () => onEditField(definition),
                },
              ]}
            />
          </Box>
        ),
      },
    ];
    return (
      <DataTable
        tableColumns={columns}
        tableData={definitions}
        getId={(definition) => definition.id}
        onRowClick={onSelectField}
        rowProps={(definition) => ({ selected: definition.id === fieldPanel?.definition.id })}
      />
    );
  },
);

const FieldPanel: FC<{
  panel: FieldPanelViewProps;
  onEditField: (definition: CustomFieldDefinition) => void;
  onClose: () => void;
}> = observer(({ panel, onEditField, onClose }) => {
  const { t } = useTranslation();
  return (
    <>
      <Box borderBottom padding justifyContent={"space-between"} alignItems={"center"}>
        <h4>{panel.definition.name}</h4>
        <Box alignItems={"center"}>
          <DropdownMenu
            button={{ type: "link", label: { key: "options" } }}
            items={[
              {
                type: "item",
                icon: "edit",
                label: { key: "entity-data.edit-field" },
                onSelect: () => onEditField(panel.definition),
              },
            ]}
          />
          <CloseButton onClick={onClose} />
        </Box>
      </Box>
      <VBox padding gap={"lg"} grow overflowY={"auto"}>
        <Tabs
          tabs={[
            {
              id: "details",
              label: t("details"),
              isActive: panel.tab === "details",
              onSelect: () => panel.onSelectTab("details"),
            },
            {
              id: "values",
              label: t("entity-data.tab-values"),
              isActive: panel.tab === "values",
              onSelect: () => panel.onSelectTab("values"),
            },
            {
              id: "log",
              label: t("entity-data.tab-log"),
              isActive: panel.tab === "log",
              onSelect: () => panel.onSelectTab("log"),
            },
          ]}
        />
        {panel.tab === "details" && <FieldPanelDetails panel={panel} />}
        {panel.tab === "values" && <FieldPanelValues panel={panel} />}
        {panel.tab === "log" && <FieldPanelLog panel={panel} />}
      </VBox>
      {panel.editValue && (
        <EditValueDialog
          model={panel.editValue.model}
          companyName={panel.editValue.row.companyName}
          subjectName={subjectDisplayName(panel.editValue.row)}
          onSubmit={panel.onSubmitEditValue}
          onCancel={panel.onCloseEditValue}
        />
      )}
      {panel.history && (
        <ValueHistoryDialog
          history={panel.history}
          title={t("entity-data.history-title", { name: panel.history.subjectName })}
          onClose={panel.onCloseValueHistory}
        />
      )}
    </>
  );
});

const FieldPanelDetails: FC<{ panel: FieldPanelViewProps }> = observer(({ panel }) => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const definition = panel.definition;
  const validations = validationEntries(definition.validations);
  const permissionRows: { key: string; role: string; label: TMessage }[] = [
    {
      key: "account-admin",
      role: t("enterprise-account.account-admin"),
      label: { key: "entity-data.permission-owner" },
    },
    {
      key: "admin",
      role: t("administrators"),
      label: permissionLabels[definition.permissions.ADMIN ?? "NONE"],
    },
    {
      key: "board-member",
      role: t("entity-data.role-board-members"),
      label: permissionLabels[definition.permissions.BOARD_MEMBER ?? "NONE"],
    },
    {
      key: "shareholder",
      role: t("entity-data.role-shareholders"),
      label: permissionLabels[definition.permissions.SHAREHOLDER ?? "NONE"],
    },
  ];
  return (
    <VBox gap={"lg"}>
      {definition.description && <div>{definition.description}</div>}
      <Grid cols={2} gap={"md"}>
        <Box fg={"subtle"}>{t("type")}</Box>
        <div>{t(dataTypeLabels[definition.dataType])}</div>
        {definition.numberFormat && (
          <>
            <Box fg={"subtle"}>{t("entity-data.formatting")}</Box>
            <div>{numberFormatSample(lang, definition.numberFormat)}</div>
          </>
        )}
        <Box fg={"subtle"}>{t("entity-data.activations")}</Box>
        <Box>
          <ActivationTag definition={definition} />
        </Box>
        {definition.dataType === "SINGLE_SELECT" && (
          <>
            <Box fg={"subtle"}>{t("entity-data.options")}</Box>
            <div>
              {definition.selectOptions
                .filter((option) => !option.isArchived)
                .map((option) => option.label)
                .join(", ")}
            </div>
          </>
        )}
        {validations.length > 0 && (
          <>
            <Box fg={"subtle"}>{t("entity-data.tab-validation")}</Box>
            <VBox gap={"xs"}>
              {validations.map((entry) => (
                <div key={entry.validation}>
                  {t(validationLabels[entry.validation])}{" "}
                  <span className={"subtle"}>
                    {entry.validation === "REQUIRED_LENGTH" && definition.requiredLength
                      ? t("entity-data.digit-count", { count: definition.requiredLength })
                      : entry.validation === "LIMIT_SELECTION_TO_ROLE" && definition.limitToRole
                        ? t(contactRoleLabels[definition.limitToRole])
                        : t(requirementLabels[entry.requirement])}
                  </span>
                </div>
              ))}
            </VBox>
          </>
        )}
      </Grid>
      <Box className={"font-semibold"}>{t("entity-data.permissions-per-role")}</Box>
      <Grid cols={2} gap={"md"}>
        {permissionRows.map((row) => (
          <Fragment key={row.key}>
            <div>{row.role}</div>
            <Box fg={"subtle"}>{t(row.label)}</Box>
          </Fragment>
        ))}
      </Grid>
    </VBox>
  );
});

const FieldPanelValues: FC<{ panel: FieldPanelViewProps }> = observer(({ panel }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <Box justifyContent={"space-between"} alignItems={"center"} gap={"md"}>
        <Box>
          <MTextInput
            model={panel.valueSearchModel}
            placeholder={t("search")}
            icon={"magnifying-glass"}
          />
          <MSelect model={panel.valueFilter} />
        </Box>
      </Box>
      {panel.definition.companyScope === "HIDDEN" ? (
        <Alert type={"info"} message={t("entity-data.values-hidden")} />
      ) : (
        <>
          {isPending(panel.valuesResult) && <LoadingIndicator />}
          {isFailure(panel.valuesResult) && <Alert type={"error"} message={t("general-error")} />}
          {isOk(panel.valuesResult) && <ValuesTable panel={panel} />}
        </>
      )}
    </VBox>
  );
});

const roleColumns: TableColumn<FieldValueRow>[] = [
  {
    name: "name",
    label: { key: "name" },
    getValue: (row) => (row.objectType === "COMPANY_ROLE" ? row.name : ""),
    align: "left",
    getSortValue: (row) => (row.objectType === "COMPANY_ROLE" ? row.name : ""),
  },
  {
    name: "role",
    label: { key: "role" },
    getValue: (row) =>
      row.objectType === "COMPANY_ROLE" ? (
        <RoleLabel role={row.role} accountRoleType={row.accountRoleType} />
      ) : (
        ""
      ),
    align: "left",
  },
];

const RoleLabel: FC<{ role: string; accountRoleType?: AccountRoleType }> = observer(
  ({ role, accountRoleType }) => {
    const { t } = useTranslation();
    return <>{t(subjectRoleLabel(role, accountRoleType))}</>;
  },
);

const companyColumn: TableColumn<FieldValueRow> = {
  name: "company",
  label: { key: "company" },
  getValue: (row) => row.companyName,
  align: "left",
  getSortValue: (row) => row.companyName,
};

const ValuesTable: FC<{ panel: FieldPanelViewProps }> = observer(({ panel }) => {
  const field = toCompanyCustomField(panel.definition);
  const columns: TableColumn<FieldValueRow>[] = [
    ...(panel.definition.objectType === "COMPANY_ROLE" ? roleColumns : []),
    companyColumn,
    {
      name: "value",
      label: { key: "entity-data.value" },
      getValue: (row) => (
        <FieldValue
          value={row.value}
          selectOptions={field.selectOptions}
          numberFormat={field.numberFormat}
        />
      ),
      align: "left",
    },
    {
      name: "actions",
      label: "",
      getValue: (row) => (
        <Box justifyContent={"end"}>
          <DropdownMenu
            button={{ type: "dotdotdot" }}
            items={[
              {
                type: "item",
                icon: "note-pencil",
                label: { key: "entity-data.update-value" },
                onSelect: () => panel.onUpdateValue(row),
              },
              {
                type: "item",
                icon: "clock-counter-clockwise",
                label: { key: "entity-data.view-history" },
                onSelect: () => panel.onShowValueHistory(row),
              },
            ]}
          />
        </Box>
      ),
    },
  ];
  return (
    <DataTable tableColumns={columns} tableData={panel.valueRows} getId={(row) => row.subject} />
  );
});

const FieldPanelLog: FC<{ panel: FieldPanelViewProps }> = observer(({ panel }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useLocaleFormat();
  const field = toCompanyCustomField(panel.definition);
  return (
    <>
      {isPending(panel.logResult) && <LoadingIndicator />}
      {isFailure(panel.logResult) && <Alert type={"error"} message={t("general-error")} />}
      {isOk(panel.logResult) && (
        <VBox gap={"md"}>
          {panel.logRows.map((row) => (
            <Box key={row.id} gap={"md"} justifyContent={"space-between"} alignItems={"start"}>
              <VBox>
                <div>{t(row.label)}</div>
                {row.value && (
                  <Box textSize={"sm"}>
                    <FieldValue
                      value={row.value}
                      selectOptions={field.selectOptions}
                      numberFormat={field.numberFormat}
                    />
                  </Box>
                )}
                <small className={"subtle"}>{row.actorName}</small>
              </VBox>
              <Box fg={"subtle"}>{formatDateTime(row.recordedAt)}</Box>
            </Box>
          ))}
        </VBox>
      )}
    </>
  );
});
